import React, { useMemo } from "react";
import type { EntityItemConfig, EntityPoolConfig, IconName } from "@salestrak/ui";
import { getEntityGlyph, type EntityData } from "#app/lib/entity-visuals";
import { EntityVisual } from "#app/components/entity";
import type { Omittable } from "@salestrak/ui";

// Issue types are commonly fetched as part of IssueViewData. Rather than
// coupling this hook to a specific container query, it accepts pre-fetched
// issue types and wraps them in a pool. This keeps the hook usable in any
// context (issue view, settings, filters, etc.).

const POOL_ID = "issue-types";

export type IssueTypeInput = Omittable<{
  issueTypeId: string;
  name: string;
  description?: string | null;
  isDefault?: boolean;
  icon?: { icon?: string | null; color?: string | null } | null;
}>;

export type UseIssueTypePoolOptions = Omittable<{
  /** Pre-fetched issue types */
  issueTypes: IssueTypeInput[];
  /** If set, shows a disabled "deleted" entry when the selected ID isn't in the list */
  deletedIssueTypeName?: string | null;
  /** The currently selected issue type ID (used for deleted type detection) */
  selectedIssueTypeId?: string | null;
}>;

/**
 * Wraps pre-fetched issue types into an EntityPoolConfig.
 */
export function useIssueTypePool(options: UseIssueTypePoolOptions) {
  const { issueTypes, deletedIssueTypeName, selectedIssueTypeId } = options;

  const items = useMemo<EntityItemConfig[]>(() => {
    const mapped: EntityItemConfig[] = issueTypes.map((t) => {
      const entity: EntityData = {
        kind: "issueType",
        id: t.issueTypeId,
        issueType: t,
      };
      // `render` drives the display (padded rounded square like teams, never a
      // circular initials avatar); the primitive icon/color stay as data so
      // selections round-trip through selectionToIconEntity.
      const glyph = getEntityGlyph(entity);
      return {
        id: t.issueTypeId,
        title: t.name,
        description: t.description ?? undefined,
        avatar: {
          icon: glyph.icon ?? undefined,
          color: glyph.color ?? undefined,
          padded: true,
          render: () => <EntityVisual entity={entity} size="md" />,
        },
        data: { isDefault: t.isDefault },
      };
    });

    if (
      deletedIssueTypeName &&
      selectedIssueTypeId &&
      !issueTypes.some((t) => t.issueTypeId === selectedIssueTypeId)
    ) {
      mapped.unshift({
        id: selectedIssueTypeId,
        title: deletedIssueTypeName,
        description: "Deleted",
        avatar: { icon: "triangle-alert" as IconName, color: "destructive", padded: true },
        disabled: true,
        data: { isDefault: false },
      });
    }

    return mapped;
  }, [issueTypes, deletedIssueTypeName, selectedIssueTypeId]);

  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: POOL_ID,
      label: "Project Types",
      items,
    }),
    [items],
  );

  return { pool };
}

export { POOL_ID as ISSUE_TYPE_POOL_ID };
